import React, { useState, useEffect } from 'react';
import { Paper, Typography, Box, Snackbar, Alert, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, IconButton, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import { UploadOrReplaceFiles } from '../../utils/uploads';
import client from '../../apollo/client';
import { ProjectImageFragment, DELETE_ATTACHMENT } from '../../graphql/projects';
import LoadingOverlay from '../common/LoadingOverlay';
import { useMutation } from '@apollo/client/react';
import ImageTab from './ShipDiagram/ImageTab';
import SvgTab from './ShipDiagram/SvgTab';
import DxfTab from './ShipDiagram/DxfTab';

import getApiUrl from '../../utils/apiUrl';

const ShipDiagram = ({ project, formIsDisabled, onImageSaved, selectedUnit, onUnitSelect }) => {
  const [imageCropSaveFunc, setImageCropSaveFunc] = useState(null);
  const [svgFile, setSvgFile] = useState(null);
  const [uploadType, setUploadType] = useState('image');
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [svgContent, setSvgContent] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [startPan, setStartPan] = useState({ x: 0, y: 0 });
  const [deleteAttachment] = useMutation(DELETE_ATTACHMENT);
  
  const getFullImageUrl = () => {
    if (!project?.projectImage?.url) return null;
    const url = getApiUrl(project.projectImage.url);
    return project.projectImage.updated_at ? `${url}?t=${project.projectImage.updated_at}` : url;
  };

  const uploadProjectImage = async (file, importData = null) => {
    if (!project?.id) return;

    setSaving(true);
    try {
      const data = await UploadOrReplaceFiles(project.id, 'WlWelds::Project', [file], {}, 'Attachments::ProjectImage', importData);
      
      if (data && data[0]) {
        const newImage = data[0];
        newImage.__typename = 'Attachment';
        
        client.writeFragment({
          id: `Project:${project.id}`,
          fragment: ProjectImageFragment,
          data: {
            projectImage: newImage,
            __typename: 'Project'
          }
        });
        
        if (onImageSaved) {
          await onImageSaved();
        }
        
        setUploadModalOpen(false);
        setShowSuccess(true);
      }
    } catch (error) {
      console.error('Error uploading project image:', error);
    } finally {
      setSaving(false);
    }
  };

  const deleteProjectImage = async () => {
    if (!project?.id || !project?.projectImage?.id) return;

    setSaving(true);
    try {
      await deleteAttachment({
        variables: {
          attachment_id: [parseInt(project.projectImage.id)],
          attachable_id: parseInt(project.id),
          attachable_type: 'WlWelds::Project'
        }
      });

      client.writeFragment({
        id: `Project:${project.id}`,
        fragment: ProjectImageFragment,
        data: {
          projectImage: null,
          __typename: 'Project'
        }
      });

      if (onImageSaved) {
        await onImageSaved();
      }

      setShowSuccess(true);
    } catch (error) {
      console.error('Error deleting project image:', error);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (project?.id) {
      setImageCropSaveFunc(() => async (file) => {
        await uploadProjectImage(file);
      });
    }
  }, [project?.id, uploadModalOpen]);

  useEffect(() => {
    if (uploadModalOpen) {
      setSvgFile(null);
      const url = project?.projectImage?.url?.toLowerCase();
      if (url?.includes('.svg')) {
        setUploadType('svg');
      } else if (url?.includes('.dxf')) {
        setUploadType('dxf');
      } else {
        setUploadType('image');
      }
    }
  }, [uploadModalOpen]);

  useEffect(() => {
    const loadSvg = async () => {
      const url = getFullImageUrl();
      if (url && url.toLowerCase().includes('.svg')) {
        try {
          const response = await fetch(url);
          const text = await response.text();
          setSvgContent(text);
        } catch (error) {
          console.error('Error loading SVG:', error);
        }
      } else {
        setSvgContent(null);
      }
    };
    loadSvg();
  }, [project?.projectImage?.url, project?.projectImage?.updated_at]);

  useEffect(() => {
    if (!svgContent) return;

    const container = document.querySelector('.svg-diagram');
    if (!container) return;

    container.querySelectorAll('g.group-area').forEach(g => g.classList.remove('highlight'));

    if (selectedUnit) {
      const group = container.querySelector(`g.group-area[id="${selectedUnit}"]`);
      if (group) {
        group.classList.add('highlight');
      }
    }
  }, [selectedUnit, svgContent]);

  useEffect(() => {
    const container = document.querySelector('.svg-diagram');
    if (!container) return;

    const handleWheelNative = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const delta = e.deltaY * -0.001;
      const newZoom = Math.min(Math.max(0.5, zoom + delta), 5);
      setZoom(newZoom);
    };

    container.addEventListener('wheel', handleWheelNative, { passive: false });
    return () => container.removeEventListener('wheel', handleWheelNative);
  }, [zoom]);

  const handleMouseDown = (e) => {
    if (e.button === 0) {
      const target = e.target.closest('g.group-area');
      if (target && target.id) {
        onUnitSelect?.(target.id);
        return;
      }
      setIsPanning(true);
      setStartPan({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isPanning) {
      e.preventDefault();
      setPan({ x: e.clientX - startPan.x, y: e.clientY - startPan.y });
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  return (
    <Paper sx={{ flex: 1, p: 3, position: 'relative' }}>
      <style>
        {`
          .svg-diagram svg { width: 100%; height: auto; }
          .svg-diagram svg g.group-area { cursor: pointer; }
          .svg-diagram svg g.group-area:hover { opacity: 0.7; }
          .svg-diagram svg g.group-area.highlight path,
          .svg-diagram svg g.group-area.highlight polyline,
          .svg-diagram svg g.group-area.highlight polygon,
          .svg-diagram svg g.group-area.highlight rect,
          .svg-diagram svg g.group-area.highlight circle { 
            stroke: #2196F3 !important; 
            stroke-width: 5 !important; 
            fill: rgba(33, 150, 243, 0.3) !important; 
          }
        `}
      </style>
      {saving && <LoadingOverlay />}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, minHeight: '48px' }}>
        <Typography variant="h6">
          Structure Diagram or Image
        </Typography>
        {!formIsDisabled && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" size="small" onClick={() => setUploadModalOpen(true)}>
              Upload Diagram / Image
            </Button>
            {getFullImageUrl() && (
              <Button variant="outlined" size="small" color="error" onClick={() => setDeleteDialogOpen(true)}>
                Delete Image
              </Button>
            )}
          </Box>
        )}
      </Box>
      <Box>
        {formIsDisabled ? (
          getFullImageUrl() && (
            <img 
              alt="project diagram" 
              src={getFullImageUrl()}
              style={{ maxWidth: '100%' }}
            />
          )
        ) : (
          <Box
            onDoubleClick={() => setUploadModalOpen(true)}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            className="svg-diagram"
            sx={{
              width: '100%',
              height: '400px',
              border: getFullImageUrl() ? '1px solid #b0d4f1' : '2px dashed #ccc',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: isPanning ? 'grabbing' : 'grab',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            {getFullImageUrl() ? (
              svgContent ? (
                <div 
                  key="svg-content"
                  dangerouslySetInnerHTML={{ __html: svgContent }}
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '100%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                    transformOrigin: 'center',
                    transition: isPanning ? 'none' : 'transform 0.1s'
                  }}
                />
              ) : (
                <img 
                  alt="project diagram" 
                  src={getFullImageUrl()}
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '100%', 
                    objectFit: 'contain',
                    transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                    transformOrigin: 'center',
                    transition: isPanning ? 'none' : 'transform 0.1s'
                  }}
                />
              )
            ) : (
              <Typography variant="body2" color="text.secondary">
                Double click to upload diagram
              </Typography>
            )}
          </Box>
        )}
      </Box>
      
      <Dialog
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Upload Diagram / Image
          <IconButton onClick={() => setUploadModalOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
            <ToggleButtonGroup
              value={uploadType}
              exclusive
              onChange={(e, newValue) => newValue && setUploadType(newValue)}
              size="small"
            >
              <ToggleButton value="image">Image Only (jpg, png, gif)</ToggleButton>
              <ToggleButton value="svg">Image + Structure (svg)</ToggleButton>
              <ToggleButton value="dxf">Image + Structure (dxf)</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {uploadType === 'image' && (
            <ImageTab 
              project={project}
              getFullImageUrl={getFullImageUrl}
              imageCropSaveFunc={imageCropSaveFunc}
            />
          )}

          {uploadType === 'svg' && (
            <SvgTab
              project={project}
              getFullImageUrl={getFullImageUrl}
              svgFile={svgFile}
              setSvgFile={setSvgFile}
              uploadProjectImage={uploadProjectImage}
            />
          )}

          {uploadType === 'dxf' && <DxfTab />}
        </DialogContent>
      </Dialog>
      
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Image</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this image? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => {
            setDeleteDialogOpen(false);
            deleteProjectImage();
          }} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success" sx={{ width: '100%' }}>
          Image saved successfully!
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default ShipDiagram;
